import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, GitMerge, Loader2, Search } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/store/toastStore';
import { getErrorMessage } from '@/services/apiClient';
import {
  getLeadMatchCandidates,
  mergeLeadToCommLog,
  type LeadMatchCandidate,
  type MergeLeadResult,
} from '@/services/leads.service';
import { searchPatients } from '@/services/patients.service';
import { getPlansByPatient, type PlanListItem } from '@/services/plans.service';
import type { PatientSummary } from '@/types/patient.types';

// ─── Helpers ─────────────────────────────────────────────────────────────────

interface PickedPatient {
  patientId: number;
  displayId: string | null;
  firstName: string;
  lastName: string;
  email: string | null;
  cellPhone: string | null;
  birthDate: string | null;
}

function fromCandidate(c: LeadMatchCandidate): PickedPatient {
  return {
    patientId: c.patientId,
    displayId: c.displayId != null ? String(c.displayId) : null,
    firstName: c.firstName,
    lastName: c.lastName,
    email: c.email,
    cellPhone: c.cellPhone,
    birthDate: c.birthDate,
  };
}

function fromSummary(p: PatientSummary): PickedPatient {
  return {
    patientId: p.patientId,
    displayId: p.displayId,
    firstName: p.firstName,
    lastName: p.lastName,
    email: p.email,
    cellPhone: p.cellPhone ?? p.phone ?? null,
    birthDate: p.dateOfBirth,
  };
}

function fullName(p: { firstName: string; lastName: string }): string {
  const name = [p.lastName, p.firstName].filter(Boolean).join(', ').trim();
  return name || 'Unknown';
}

/**
 * Legacy color tiers from message-lead-display-fs.php L917-928.
 * Score=1→#f0feea, 2→#e5fedb, 3→#ddfed0, 4+→#d4ffc3.
 */
function matchBackground(score: number): string {
  if (score >= 4) return 'bg-emerald-100';
  if (score === 3) return 'bg-emerald-50';
  if (score === 2) return 'bg-green-50';
  if (score === 1) return 'bg-lime-50';
  return 'bg-slate-50';
}

function formatPlanDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

// ─── Component ───────────────────────────────────────────────────────────────

type Step = 'patient' | 'plan';

export interface LeadMergeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  leadId: number;
  leadKind?: 'email' | 'sms';
  /** Subject/topic preview shown above the picker. Optional. */
  subjectPreview?: string | null;
  /** Pre-select a patient (skips step 1). Used by the sidebar "Merge" affordance. */
  initialPatient?: PickedPatient | null;
  onMerged?: (result: MergeLeadResult) => void;
}

export function LeadMergeDialog({
  isOpen,
  onClose,
  leadId,
  leadKind = 'email',
  subjectPreview,
  initialPatient = null,
  onMerged,
}: LeadMergeDialogProps) {
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>(initialPatient ? 'plan' : 'patient');
  const [candidates, setCandidates] = useState<LeadMatchCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<PatientSummary[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [picked, setPicked] = useState<PickedPatient | null>(initialPatient);
  // PlanListItem, which is what getPlansByPatient returns. Typed as Plan it
  // was a different model, and both setPlans calls were rejected — the rows
  // rendered here (planId, name, planTypeName, lastContact, proc1Name,
  // proc2Name, stageName) all exist on PlanListItem.
  const [plans, setPlans] = useState<PlanListItem[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [planId, setPlanId] = useState<number>(0);
  const [mergeCommType, setMergeCommType] = useState<0 | 1>(0);
  const [mergeCopyToNote, setMergeCopyToNote] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);

  const searchDebounceRef = useRef<number | null>(null);

  // ─── Reset state on open/close ───────────────────────────────────────────

  useEffect(() => {
    if (!isOpen) {
      setStep(initialPatient ? 'plan' : 'patient');
      setCandidates([]);
      setSearchTerm('');
      setSearchResults([]);
      setPicked(initialPatient);
      setPlans([]);
      setPlanId(0);
      setMergeCommType(0);
      setMergeCopyToNote(false);
      setSubmitting(false);
      return;
    }

    if (initialPatient) {
      // Skip Step 1; jump straight to plan picker for the pre-selected patient.
      setPicked(initialPatient);
      setStep('plan');
      setPlansLoading(true);
      getPlansByPatient(initialPatient.patientId)
        .then((rows) => setPlans(rows))
        .catch(() => setPlans([]))
        .finally(() => setPlansLoading(false));
      return;
    }

    setCandidatesLoading(true);
    getLeadMatchCandidates(leadId, leadKind)
      .then((rows) => setCandidates(rows))
      .catch(() => setCandidates([]))
      .finally(() => setCandidatesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, leadId, leadKind]);

  // ─── Auto-advance to Step 2 when there's exactly one strong match ────────
  //
  // Legacy parity: message-lead-display-fs.php L1256-1259 auto-loads the only
  // match via the inline `onload='loadPatientIntoLead(...)'` when
  // `count($posMatchArray) === 1`. posMatchArray is built from candidates
  // where `nameMatch === 1 && (dobMatch || emailMatch || phoneMatch)`.

  useEffect(() => {
    if (!isOpen || initialPatient || step !== 'patient' || candidatesLoading) return;
    const strong = candidates.filter(
      (c) => c.matches.name && (c.matches.dob || c.matches.email || c.matches.phone),
    );
    if (strong.length === 1) {
      handlePickPatient(fromCandidate(strong[0]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialPatient, step, candidatesLoading, candidates]);

  // ─── Patient search (debounced) ──────────────────────────────────────────

  useEffect(() => {
    if (searchDebounceRef.current) {
      window.clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    const term = searchTerm.trim();
    if (term.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    searchDebounceRef.current = window.setTimeout(() => {
      searchPatients(term)
        .then((rows) => setSearchResults(rows))
        .catch(() => setSearchResults([]))
        .finally(() => setSearchLoading(false));
    }, 250);

    return () => {
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [searchTerm]);

  // ─── Picking & merging ───────────────────────────────────────────────────

  const handlePickPatient = useCallback((next: PickedPatient) => {
    setPicked(next);
    setPlanId(0);
    setStep('plan');

    setPlansLoading(true);
    getPlansByPatient(next.patientId)
      .then((rows) => setPlans(rows))
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false));
  }, []);

  const handleMerge = useCallback(async () => {
    if (picked == null) return;

    setSubmitting(true);
    try {
      const result = await mergeLeadToCommLog(leadId, {
        patientId: picked.patientId,
        planId: planId || 0,
        mergeCommType,
        mergeCopyToNote,
        leadKind,
      });

      toast.success('Merged', `Lead linked to ${fullName(picked)}.`);

      // Invalidate caches that may have surfaced the lead or comm-log entry.
      void queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
      void queryClient.invalidateQueries({ queryKey: ['lead-counts'] });
      // The CRM nav bubble counts unworked leads, so a status change moves it.
      void queryClient.invalidateQueries({ queryKey: ['navigation', 'badges'] });
      void queryClient.invalidateQueries({ queryKey: ['communications'] });
      void queryClient.invalidateQueries({ queryKey: ['comm-log'] });
      if (result.planId > 0) {
        void queryClient.invalidateQueries({ queryKey: ['plan', result.planId] });
        void queryClient.invalidateQueries({ queryKey: ['plans'] });
      }
      void queryClient.invalidateQueries({ queryKey: ['patient', result.patientId] });

      onMerged?.(result);
      onClose();
    } catch (error) {
      toast.error('Merge failed', getErrorMessage(error, 'Unable to merge lead.'));
    } finally {
      setSubmitting(false);
    }
  }, [leadId, leadKind, mergeCommType, mergeCopyToNote, onClose, onMerged, picked, planId, queryClient]);

  // ─── Render ──────────────────────────────────────────────────────────────

  const subjectLine = useMemo(() => {
    const raw = (subjectPreview ?? '').trim();
    if (!raw) return '';
    return raw.length > 80 ? raw.slice(0, 80) + '…' : raw;
  }, [subjectPreview]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={step === 'patient' ? 'Merge to Comm Log — Pick Patient' : 'Merge to Comm Log — Pick Plan'}
      size="xl"
      footer={
        step === 'plan' ? (
          <>
            {!initialPatient && (
              <button
                type="button"
                onClick={() => setStep('patient')}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            )}
            <button
              type="button"
              onClick={() => void handleMerge()}
              disabled={submitting || picked == null}
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitMerge className="h-4 w-4" />}
              Merge
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
        )
      }
    >
      {subjectLine && (
        <div className="mb-4 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
          <span className="font-medium text-slate-700">Message:</span> {subjectLine}
        </div>
      )}

      {step === 'patient' && (
        <div className="flex flex-col gap-5">
          <section>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Possible Matches</h3>
            {candidatesLoading ? (
              <div className="flex items-center justify-center py-6">
                <Spinner size="sm" />
              </div>
            ) : candidates.length === 0 ? (
              <p className="rounded-md bg-slate-50 px-3 py-3 text-sm text-slate-500">No matches found.</p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {candidates.map((c) => (
                  <li key={c.patientId}>
                    <button
                      type="button"
                      onClick={() => handlePickPatient(fromCandidate(c))}
                      className={`group flex w-full flex-col gap-1 rounded-md border border-slate-200 px-3 py-2 text-left transition-colors hover:border-blue-400 hover:bg-blue-50 ${matchBackground(c.matchScore)}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-slate-900">
                          {fullName(c)}{c.displayId ? ` (#${c.displayId})` : ''}
                        </span>
                        {c.matchScore > 0 && (
                          <span className="text-xs text-slate-500">match score {c.matchScore}</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-slate-600">
                        {c.email && (
                          <span className={c.matches.email ? 'font-semibold text-emerald-700' : ''}>
                            {c.matches.email && <Check className="mr-0.5 inline h-3 w-3" />}
                            {c.email}
                          </span>
                        )}
                        {c.cellPhone && (
                          <span className={c.matches.phone ? 'font-semibold text-emerald-700' : ''}>
                            {c.matches.phone && <Check className="mr-0.5 inline h-3 w-3" />}
                            {c.cellPhone}
                          </span>
                        )}
                        {c.birthDate && (
                          <span className={c.matches.dob ? 'font-semibold text-emerald-700' : ''}>
                            {c.matches.dob && <Check className="mr-0.5 inline h-3 w-3" />}
                            DOB {c.birthDate}
                          </span>
                        )}
                        {c.city && c.state && (
                          <span>{c.city}, {c.state}</span>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Search Patients</h3>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type at least 2 characters to search..."
                className="w-full rounded-md border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {searchTerm.trim().length >= 2 && (
              <div className="mt-2">
                {searchLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Spinner size="sm" />
                  </div>
                ) : searchResults.length === 0 ? (
                  <p className="rounded-md bg-slate-50 px-3 py-3 text-sm text-slate-500">No patients found.</p>
                ) : (
                  <ul className="flex flex-col gap-1">
                    {searchResults.map((p) => (
                      <li key={p.patientId}>
                        <button
                          type="button"
                          onClick={() => handlePickPatient(fromSummary(p))}
                          className="flex w-full flex-col gap-0.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-left hover:border-blue-400 hover:bg-blue-50"
                        >
                          <span className="text-sm font-semibold text-slate-900">
                            {fullName(p)}{p.displayId ? ` (#${p.displayId})` : ''}
                          </span>
                          <span className="text-xs text-slate-500">
                            {[p.email, p.cellPhone || p.phone].filter(Boolean).join(' · ') || '—'}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </section>
        </div>
      )}

      {step === 'plan' && picked && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <div>
              <span className="block text-xs uppercase tracking-wide text-slate-500">Selected patient</span>
              <span className="text-sm font-semibold text-slate-900">
                {fullName(picked)}{picked.displayId ? ` (#${picked.displayId})` : ''}
              </span>
            </div>
            {!initialPatient && (
              <button
                type="button"
                onClick={() => setStep('patient')}
                className="text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                Change
              </button>
            )}
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Plan</h3>
            {plansLoading ? (
              <div className="flex items-center justify-center py-6">
                <Spinner size="sm" />
              </div>
            ) : (
              <table className="w-full overflow-hidden rounded-md border border-slate-200 text-sm">
                <thead className="bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="w-10 px-3 py-2"></th>
                    <th className="px-3 py-2">Plan</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Last Contact</th>
                    <th className="px-3 py-2">Procedure</th>
                    <th className="px-3 py-2">Stage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  <tr
                    className={planId === 0 ? 'bg-blue-50' : ''}
                    onClick={() => setPlanId(0)}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="radio"
                        name="lead-merge-plan"
                        value="0"
                        checked={planId === 0}
                        onChange={() => setPlanId(0)}
                      />
                    </td>
                    <td className="px-3 py-2 font-semibold text-slate-700">None</td>
                    <td className="px-3 py-2 text-slate-400">—</td>
                    <td className="px-3 py-2 text-slate-400">—</td>
                    <td className="px-3 py-2 text-slate-400">—</td>
                    <td className="px-3 py-2 text-slate-400">—</td>
                  </tr>
                  {plans.map((plan) => (
                    <tr
                      key={plan.planId}
                      className={planId === plan.planId ? 'bg-blue-50' : ''}
                      onClick={() => setPlanId(plan.planId)}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="radio"
                          name="lead-merge-plan"
                          value={plan.planId}
                          checked={planId === plan.planId}
                          onChange={() => setPlanId(plan.planId)}
                        />
                      </td>
                      <td className="px-3 py-2">{plan.name || `Plan #${plan.planId}`}</td>
                      <td className="px-3 py-2">{plan.planTypeName || '—'}</td>
                      <td className="px-3 py-2">{formatPlanDate(plan.lastContact)}</td>
                      <td className="px-3 py-2">
                        {[plan.proc1Name, plan.proc2Name].filter(Boolean).join(', ') || '—'}
                      </td>
                      <td className="px-3 py-2">{plan.stageName || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Legacy options row from lib/lead-merge-plan-list.php L93-104. */}
          <div className="flex flex-wrap items-center gap-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={mergeCopyToNote}
                onChange={(e) => setMergeCopyToNote(e.target.checked)}
              />
              Copy to Notes
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <span>Visibility:</span>
              <select
                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={String(mergeCommType)}
                onChange={(e) => setMergeCommType(e.target.value === '1' ? 1 : 0)}
              >
                <option value="0">Not visible to patient</option>
                <option value="1">Visible to patient</option>
              </select>
            </label>
          </div>
        </div>
      )}
    </Modal>
  );
}
